/**
 * O que falta para a pessoa entrar nos servidores com o proprio usuario do LDAP
 * (consoleai#26229, spec 2026-11-03-senha-unix-e-aviso-de-acesso secao 3.5).
 *
 * Uma query so para o banner do layout, o alerta da pagina Servidores e o cartao
 * do perfil: os tres dizem a mesma coisa, e com tres queries um deles ficaria
 * desatualizado depois de definir a senha. `staleTime` de minutos porque o dado
 * so muda quando a propria pessoa define a senha (e ai a tela invalida) ou
 * quando alguem mexe no grupo do LDAP, que nao e coisa de segundos.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { profileApi, type AcessoServidores } from '@/api/profile'

export const CHAVE_ACESSO_SERVIDORES = ['profile', 'acesso-servidores'] as const

export const ANCORA_SENHA_UNIX = '/perfil#senha-unix'

export function useAcessoServidores() {
  return useQuery({
    queryKey: CHAVE_ACESSO_SERVIDORES,
    queryFn: profileApi.acessoServidores,
    staleTime: 5 * 60_000,
    // Aviso e conveniencia: se a consulta falhou, nao ha o que dizer, e insistir
    // so gera carga no bind do Kanidm que a rota faz.
    retry: false,
  })
}

export function useInvalidarAcessoServidores() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: CHAVE_ACESSO_SERVIDORES })
}

export interface AvisoDeAcesso {
  tipo: 'sem_grupo_de_login' | 'sem_senha_unix'
  /** `danger` quando o servidor recusa a entrada; `warning` quando so o sudo falha. */
  gravidade: 'danger' | 'warning'
  texto: string
  acao?: { rotulo: string; para: string }
}

/**
 * Decide os avisos a partir da resposta da API.
 *
 * - Sem dados (carregando ou falhou) ou sem tenant: nada. Quem nao abre shell
 *   pessoal em lugar nenhum entra como `ssm-user`, e a senha UNIX nao lhe serve.
 * - `tenantId`, quando dado (pagina Servidores), restringe ao tenant aberto: num
 *   tenant fora do shell pessoal a sessao nem usa o usuario da pessoa.
 * - `sem_grupo_de_login === null` e "o Kanidm nao respondeu", nunca "sem grupo":
 *   tratar como verdadeiro avisaria todo mundo numa queda do Kanidm.
 * - O de grupo vem primeiro: sem ele o servidor recusa a entrada, e a senha UNIX
 *   nem chega a ser pedida.
 */
export function avisosDeAcesso(
  dados: AcessoServidores | null | undefined,
  opcoes: { tenantId?: number } = {},
): AvisoDeAcesso[] {
  if (!dados || dados.tenants.length === 0) return []
  if (opcoes.tenantId !== undefined && !dados.tenants.some((t) => t.id === opcoes.tenantId)) {
    return []
  }
  const avisos: AvisoDeAcesso[] = []
  if (dados.sem_grupo_de_login === true) {
    const grupos = dados.grupos_de_login.length > 0
      ? dados.grupos_de_login.join(' ou ')
      : 'devops ou login_users'
    avisos.push({
      tipo: 'sem_grupo_de_login',
      gravidade: 'danger',
      texto: `O servidor vai recusar a sua entrada: você não está nos grupos ${grupos} do LDAP. Peça ao time de plataforma.`,
    })
  }
  if (dados.sem_senha_unix) {
    avisos.push({
      tipo: 'sem_senha_unix',
      gravidade: 'warning',
      texto: 'Você tem acesso a servidores com o seu usuário do LDAP, mas ainda não definiu a senha UNIX que o sudo pede.',
      acao: { rotulo: 'Definir a senha UNIX', para: ANCORA_SENHA_UNIX },
    })
  }
  return avisos
}
